/**
 * INICIO — FASE 3.3-A
 *
 * `/` deja de redirigir a `/clientes` (una pantalla para la que OPERARIO y
 * CLIENTE no tienen permiso) y pasa a ser una página real, segura para
 * cualquier sesión: saludo, rol visible, y accesos directos SÓLO a los
 * módulos que `puede()` ya autoriza — el mismo filtro que decide qué
 * grupos de `NAV` se pintan en la barra lateral.
 *
 * SIN DATOS, A PROPÓSITO
 *
 * Esta página no llama a la API. Es la manera más simple de cumplir «sin
 * errores, sin redirigir a Clientes» para un usuario sin permisos
 * funcionales todavía (OPERARIO/CLIENTE): no hay nada que pueda fallar.
 * Contadores u otro contenido que si necesite datos son una ampliación
 * posterior, no parte de este mínimo.
 *
 * F10.1: esa ampliación es «Hoy» (`hoy.tsx`), y sólo para quien gestiona OT
 * (`ots:leer`, sin cliente): OPERARIO sigue viendo esta página sin ninguna
 * llamada a la API.
 *
 * PORTAL CLIENTE v2: el CLIENTE (`ots:leer_propias`, con cliente) empieza por el
 * estado de su servicio (`portal-inicio.tsx`) — lo que antes le faltaba aquí.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { destinoEntrada, entradaVisible, NAV, Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { tieneCapacidadOffline } from '../offline/paquete.ts';
import { etiquetaRol } from '../roles.ts';
import { SeccionHoy } from './hoy.tsx';
import { SeccionServicioCliente } from './portal-inicio.tsx';

export function PaginaInicio(): ReactNode {
  const { usuario, puede } = useSesion();

  const rolesVisibles = [...new Set((usuario?.roles ?? []).map((r) => etiquetaRol(r.rol)))].join(
    ' · ',
  );

  const gruposVisibles = NAV.map((g) => ({
    ...g,
    entradas: g.entradas.filter((e) => entradaVisible(e, puede)),
  })).filter((g) => g.entradas.length > 0);

  //  ENCARGADO/OPERARIO no llevan NINGÚN permiso de `NAV` para esto
  //  (OPERARIO, de hecho, no lleva ninguno en absoluto — ver la cabecera
  //  de `shell.tsx`) así que este acceso vive fuera del sistema de
  //  permisos de la barra lateral, igual que "Inicio" mismo: un chequeo
  //  de ROL directo, sólo para decidir si se muestra la tarjeta.
  const puedeOffline = usuario !== null && tieneCapacidadOffline(usuario.roles);
  //  F10.1: quien gestiona OT de Limpieza (ENCARGADO, ADMIN, CONSULTOR) empieza por su día.
  const conHoy = usuario !== null && usuario.idCliente === null && puede('ots:leer');
  const conServicio = usuario !== null && usuario.idCliente !== null && puede('ots:leer_propias');

  return (
    <Shell titulo="Inicio">
      {conHoy && <SeccionHoy />}
      {conServicio && <SeccionServicioCliente />}

      <div className="panel">
        <div className="panel-cuerpo">
          <h2 style={{ marginTop: 0 }}>
            Hola, {usuario?.nombre} {usuario?.apellidos}
          </h2>
          <p className="suave">{rolesVisibles === '' ? 'Sin rol asignado' : rolesVisibles}</p>
        </div>
      </div>

      {puedeOffline && (
        <div className="panel">
          <div className="panel-cuerpo">
            <h2 style={{ marginTop: 0 }}>Trabajar sin conexión</h2>
            <p className="suave">
              Descarga tus órdenes de trabajo para consultarlas aunque te quedes sin cobertura.
            </p>
            <Link to="/offline" className="etiqueta info">Ver datos sin conexión</Link>
          </div>
        </div>
      )}

      {gruposVisibles.length === 0 ? (
        <div className="panel">
          <div className="panel-cuerpo">
            <p>
              Tu cuenta todavía no tiene ningún módulo habilitado en NeatOS. En cuanto lo tenga,
              aparecerá aquí.
            </p>
          </div>
        </div>
      ) : (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Accesos directos</h2>
          </div>
          <div className="panel-cuerpo">
            {gruposVisibles.map((g) => (
              <div key={g.titulo} className="sep">
                <div className="etiqueta-dato">{g.titulo}</div>
                <div className="fila" style={{ flexWrap: 'wrap' }}>
                  {g.entradas.map((entrada) => (
                    <Link key={entrada.a} to={destinoEntrada(entrada, puede)} className="etiqueta info">
                      {entrada.texto}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
